import type { ProductsByPlantFormikType } from '@core/formik/quote';
import type { FixedPricingByCompany } from '@core/types/api/FixedPricingByCompany/FixedPricingByCompany';
import { hasStringValue, hasValue } from '@core/utils/equality';
import type { YupSchemaNullableReferenceType } from '@core/utils/yupHelpers';

export interface QuoteContact {
  id: string | null;
  option: { value: string; label: string } | null;
  isNewContact?: boolean;
  contact?: {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    jobTitle: string;
    company: YupSchemaNullableReferenceType;
  };
}

export const getPricingChipLabel = (
  hasTierPricing: boolean,
  fixedPricingByCompany: FixedPricingByCompany | undefined,
  allProductsByPlants: ProductsByPlantFormikType[],
): string | null => {
  if (!hasTierPricing || !hasValue(fixedPricingByCompany)) {
    return null;
  }

  let hasFixed = false;
  let hasImported = false;
  let hasAdjusted = false;

  for (const plantGroup of allProductsByPlants) {
    if (hasFixed && hasImported && hasAdjusted) {
      // the next iterations will not tell anything new
      break;
    }

    const plantId = plantGroup.plant?.id;
    if (hasStringValue(plantId)) {
      const location = fixedPricingByCompany[plantId];
      if (location) {
        for (const quoteProduct of plantGroup.products) {
          const productId = quoteProduct.product?.id;
          if (hasStringValue(productId)) {
            if (hasValue(location.products[productId])) {
              hasFixed = true;
            } else if (hasValue(location.importedProducts?.[productId])) {
              hasImported = true;
            } else if (hasValue(location.adjustment)) {
              hasAdjusted = true;
            }
          }
        }
      }
    }
  }

  const pricing: string[] = [];
  if (hasFixed) {
    pricing.push('Fixed');
  }
  if (hasImported) {
    pricing.push('Imported');
  }
  if (hasAdjusted) {
    pricing.push('Tier');
  }

  if (pricing.length === 0) {
    return null;
  }
  return `${pricing.join(' & ')} pricing applied`;
};

export const getValidCompanyId = (company: { id: string | null } | null | undefined): string => {
  return hasValue(company) && hasStringValue(company.id) ? company.id : '';
};

export const isQuoteContact = (
  contact: YupSchemaNullableReferenceType | null,
): contact is QuoteContact => {
  return hasValue(contact) && ('isNewContact' in contact || 'id' in contact);
};
